import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
    init as coreInit,
    cache,
    RenderingEngine,
    Enums as csEnums,
    setVolumesForViewports,
    volumeLoader,
    imageLoader,
    metaData
} from '@cornerstonejs/core';
import {
    init as initTools,
    addTool,
    WindowLevelTool,
    PanTool,
    ZoomTool,
    LengthTool,
    AngleTool,
    ProbeTool,
    EllipticalROITool,
    RectangleROITool,
    StackScrollTool,
    ArrowAnnotateTool,
    BidirectionalTool,
    CobbAngleTool,
    ToolGroupManager,
    Enums as ToolEnums,
    annotation
} from '@cornerstonejs/tools';
import * as cornerstone from 'cornerstone-core';
import * as cornerstoneWADOImageLoader from 'cornerstone-wado-image-loader';
import dicomParser from 'dicom-parser';
import { Box, Typography, Button, Tabs, Tab } from '@mui/material';
import { readDICOMFile, getTransferSyntaxDescription, isMP4TransferSyntax } from '../utils/cornerstoneInit';
import MeasurementPanel from './MeasurementPanel';
import EnhancedMeasurementPanel from './EnhancedMeasurementPanel';
import { MEASUREMENT_TOOLS } from '../utils/echoMeasurements';
import {
    PixelSpacingManager,
    MeasurementProcessor,
    MeasurementKeyboardManager,
    MEASUREMENT_TOOL_TYPES
} from '../utils/measurementTools';
import {
    registerAHIImageLoaderCS3D,
    setupAHIViewport,
    setupAHIToolGroup,
    activateAHIMeasurementTool,
    cleanupAHIViewport
} from '../utils/cornerstoneAHIIntegration';
import CompactPocusReport from './CompactPocusReport';
import DicomTagsDialog from './DicomTagsDialog';
import './DicomUnifiedViewer.css';

// Global initialization flag
let globalInitialized = false;

/**
 * Unified DICOM Viewer that automatically uses the appropriate viewport type
 * - STACK viewport for standard DICOM files
 * - VIDEO viewport for MP4-encoded DICOM files
 * - AHI viewport for AWS HealthImaging streamed data
 */
const DicomUnifiedViewer = React.forwardRef(({ 
    preloadedFile, 
    preloadedImageId, 
    fileName, 
    isAHI = false, 
    ahiMetadata, 
    patientInfo, 
    onGridLayoutChange, 
    currentGridLayout, 
    onDrawerToggle, 
    isGridMode = false,
    // Annotation props from parent
    annotationMode: parentAnnotationMode,
    activeTool: parentActiveTool,
    currentColor: parentCurrentColor,
    currentLineWidth: parentCurrentLineWidth,
    onAnnotationModeChange,
    onActiveToolChange
}, ref) => {
    const viewportRef = useRef(null);
    const [fileInfo, setFileInfo] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentFrame, setCurrentFrame] = useState(1);
    const [totalFrames, setTotalFrames] = useState(1);
    const [frameRate, setFrameRate] = useState(30);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const [showControls, setShowControls] = useState(false);
    const [studyNotes, setStudyNotes] = useState('');
    const [rightDrawerOpen, setRightDrawerOpen] = useState(true); // Open by default
    const [activeTab, setActiveTab] = useState(0); // 0: Tools, 1: Measure, 2: Report, 3: Notes
    const [activeTool, setActiveTool] = useState('WindowLevel');
    const [windowWidth, setWindowWidth] = useState(null);
    const [windowCenter, setWindowCenter] = useState(null);
    const [annotationMode, setAnnotationMode] = useState(false);
    const [isDrawing, setIsDrawing] = useState(false);
    const [annotations, setAnnotations] = useState({});
    const [currentColor, setCurrentColor] = useState('#FF0000');
    const [currentLineWidth, setCurrentLineWidth] = useState(2);
    const [activeMeasurementType, setActiveMeasurementType] = useState(null);
    const [measurementResults, setMeasurementResults] = useState({});
    const [toolGroupId] = useState('myToolGroup');
    const toolGroupRef = useRef(null);
    const annotationCanvasRef = useRef(null);
    const canvasesRef = useRef({}); // Store canvases per SOP
    const renderingEngineRef = useRef(null);
    const extractedVideoUrlRef = useRef(null);
    const videoElementRef = useRef(null);
    const animationFrameRef = useRef(null);
    const [currentSopInstanceUID, setCurrentSopInstanceUID] = useState(null);
    
    // Context menu state
    const [contextMenu, setContextMenu] = useState(null);
    const [showDicomTagsDialog, setShowDicomTagsDialog] = useState(false);
    
    // Annotation persistence state
    const [showAnnotations, setShowAnnotations] = useState(true);

    // Save annotations to localStorage
    const saveAnnotations = useCallback((sopId, annotationsData) => {
        try {
            const key = `annotations_${sopId}`;
            localStorage.setItem(key, JSON.stringify(annotationsData));
            console.log(`Saved annotations for SOP: ${sopId}`);
        } catch (error) {
            console.error('Error saving annotations:', error);
        }
    }, []);

    // Load annotations from localStorage
    const loadAnnotations = useCallback((sopId) => {
        try {
            const key = `annotations_${sopId}`;
            const saved = localStorage.getItem(key);
            if (saved) {
                const parsedAnnotations = JSON.parse(saved);
                console.log(`Loaded annotations for SOP: ${sopId}`, parsedAnnotations);
                return parsedAnnotations;
            }
        } catch (error) {
            console.error('Error loading annotations:', error);
        }
        return [];
    }, []);

    // Auto-save annotations when they change
    useEffect(() => {
        if (currentSopInstanceUID && annotations[currentSopInstanceUID]) {
            saveAnnotations(currentSopInstanceUID, annotations[currentSopInstanceUID]);
        }
    }, [annotations, currentSopInstanceUID, saveAnnotations]);

    // Load annotations when SOP changes
    useEffect(() => {
        if (currentSopInstanceUID) {
            const savedAnnotations = loadAnnotations(currentSopInstanceUID);
            if (savedAnnotations.length > 0) {
                setAnnotations(prev => ({
                    ...prev,
                    [currentSopInstanceUID]: savedAnnotations
                }));
            }
        }
    }, [currentSopInstanceUID, loadAnnotations]);

    // Toggle annotation visibility
    const toggleAnnotationVisibility = () => {
        setShowAnnotations(!showAnnotations);
        // Redraw to show/hide annotations
        setTimeout(() => redrawAnnotations(), 50);
    };

    // Handle right-click context menu
    const handleContextMenu = (e) => {
        e.preventDefault();
        setContextMenu({
            mouseX: e.clientX - 2,
            mouseY: e.clientY - 4,
        });
    };

    // Close context menu
    const handleCloseContextMenu = () => {
        setContextMenu(null);
    };

    // Handle context menu actions
    const handleContextMenuAction = (action) => {
        setContextMenu(null);
        
        switch (action) {
            case 'dicomTags':
                setShowDicomTagsDialog(true);
                break;
            case 'saveAsDicom':
                console.log('Save as DICOM - Not implemented yet');
                break;
            case 'saveAsVideo':
                console.log('Save as Video - Not implemented yet');
                break;
            case 'saveAsImage':
                console.log('Save as Image - Not implemented yet');
                break;
            default:
                break;
        }
    };

    // Close context menu when clicking elsewhere
    useEffect(() => {
        const handleClickOutside = () => {
            if (contextMenu) {
                setContextMenu(null);
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [contextMenu]);

    // Handle drawer tab change
    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        setRightDrawerOpen(true);
    };

    // Toggle right drawer
    const toggleRightDrawer = () => {
        const newState = !rightDrawerOpen;
        setRightDrawerOpen(newState);
        if (onDrawerToggle) {
            onDrawerToggle(newState);
        }
    };

    // Cleanup function
    const cleanupResources = useCallback(() => {
        // Cancel any animation frames
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
        }

        // Clean up video element
        if (videoElementRef.current) {
            videoElementRef.current.pause();
            videoElementRef.current.src = '';
            videoElementRef.current = null;
        }

        // Clean up tool group
        if (toolGroupRef.current) {
            try {
                ToolGroupManager.destroyToolGroup(toolGroupId);
            } catch (e) {
                console.log('Tool group cleanup error:', e);
            }
            toolGroupRef.current = null;
        }

        // Clean up regular Cornerstone
        const element = viewportRef.current;
        if (element) {
            try {
                if (cornerstone.getEnabledElement(element)) {
                    cornerstone.disable(element);
                }
            } catch (e) {
                // Element not enabled
            }
        }

        // Clean up Cornerstone3D rendering engine
        if (renderingEngineRef.current) {
            try {
                if (typeof renderingEngineRef.current.destroy === 'function') {
                    renderingEngineRef.current.destroy();
                }
            } catch (e) {
                // Silently catch the error
            }
            renderingEngineRef.current = null;
        }

        if (extractedVideoUrlRef.current) {
            URL.revokeObjectURL(extractedVideoUrlRef.current);
            extractedVideoUrlRef.current = null;
        }

        // Clear cache
        try {
            cache.purgeCache();
        } catch (e) {
            // Cache might not be initialized
        }
    }, [toolGroupId]);

    // Initialize Cornerstone3D and regular Cornerstone
    useEffect(() => {
        const init = async () => {
            try {
                if (!globalInitialized) {
                    console.log('Initializing viewers globally...');

                    // Initialize Cornerstone3D
                    await coreInit();
                    await initTools();

                    // Add all tools including measurement tools
                    const toolsToAdd = [
                        WindowLevelTool,
                        PanTool,
                        ZoomTool,
                        LengthTool,
                        AngleTool,
                        ProbeTool,
                        EllipticalROITool,
                        RectangleROITool,
                        StackScrollTool,
                        ArrowAnnotateTool,
                        BidirectionalTool,
                        CobbAngleTool
                    ];

                    toolsToAdd.forEach(tool => {
                        try {
                            addTool(tool);
                        } catch (e) {
                            console.log(`Tool ${tool.toolName} might already be added`);
                        }
                    });

                    // Register AHI image loader for Cornerstone3D
                    registerAHIImageLoaderCS3D();

                    // Expose cornerstone and cornerstoneTools globally for video measurements
                    // This matches the Cornerstone demo setup
                    window.cornerstone = require('@cornerstonejs/core');
                    window.cornerstoneTools = require('@cornerstonejs/tools');

                    globalInitialized = true;
                }

                // Initialize regular Cornerstone for STACK viewport (can be done multiple times)
                cornerstone.imageCache.setMaximumSizeBytes(3000000000);
                cornerstoneWADOImageLoader.external.cornerstone = cornerstone;
                cornerstoneWADOImageLoader.external.dicomParser = dicomParser;

                // Configure WADO loader
                if (!cornerstoneWADOImageLoader.webWorkerManager.initialized) {
                    cornerstoneWADOImageLoader.webWorkerManager.initialize({
                        maxWebWorkers: navigator.hardwareConcurrency || 1,
                        startWebWorkersOnDemand: true,
                        taskConfiguration: {
                            decodeTask: {
                                initializeCodecsOnStartup: false,
                                usePDFJS: false,
                            },
                        },
                    });
                }

                cornerstoneWADOImageLoader.configure({
                    useWebWorkers: true,
                    decodeConfig: {
                        convertFloatPixelDataToInt: false,
                    },
                });

                console.log('Viewer instance initialized successfully');
                setIsInitialized(true);
            } catch (error) {
                console.error('Error initializing viewers:', error);
                setError(`Failed to initialize viewer: ${error.message}`);
            }
        };

        if (!isInitialized) {
            init();
        }

        // Cleanup on unmount
        return () => {
            cleanupResources();
        };
    }, [isInitialized, cleanupResources]);

    // Setup tool group for measurements
    const setupToolGroup = useCallback(() => {
        if (!viewportRef.current) return;

        // Skip if this is an AHI image - tool group is handled separately
        if (fileInfo?.isAHI && !fileInfo?.isMP4) {
            console.log('Skipping cornerstone-core tool group setup for AHI image');
            return;
        }

        // Skip if we already have a proper AHI tool group set up
        if (toolGroupRef.current && toolGroupRef.current._isAHIToolGroup) {
            console.log('Skipping tool group setup - AHI tool group already configured');
            return;
        }

        try {
            const element = viewportRef.current;
            
            // For MP4 videos, only set up simple video tool group when paused
            if (fileInfo?.isMP4) {
                if (isPlaying) {
                    console.log('MP4 video is playing - skipping tool setup');
                    return;
                }
                
                // Check if we already have a video tool group set up
                if (toolGroupRef.current && toolGroupRef.current.isVideo) {
                    console.log('MP4 video tool group already set up, skipping');
                    return;
                }
                
                console.log('MP4 video is paused - setting up simple measurement tools');
                
                // Enable the element with cornerstone-core for measurements
                try {
                    // Check if already enabled
                    let enabledElement;
                    try {
                        enabledElement = cornerstone.getEnabledElement(element);
                    } catch (e) {
                        // Not enabled, so enable it
                        cornerstone.enable(element);
                        enabledElement = cornerstone.getEnabledElement(element);
                    }
                    
                    // Create a synthetic image from the current video frame
                    const video = videoElementRef.current;
                    if (video && video.videoWidth && video.videoHeight) {
                        const canvas = document.createElement('canvas');
                        canvas.width = video.videoWidth;
                        canvas.height = video.videoHeight;
                        const ctx = canvas.getContext('2d');
                        ctx.drawImage(video, 0, 0);
                        
                        // Create a synthetic cornerstone image
                        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                        const syntheticImageId = `video:${fileName}:frame:${currentFrame}`;
                        
                        // Create Cornerstone3D compatible image
                        const syntheticImage = {
                            imageId: syntheticImageId,
                            minPixelValue: 0,
                            maxPixelValue: 255,
                            slope: 1,
                            intercept: 0,
                            windowCenter: 128,
                            windowWidth: 256,
                            render: cornerstone.renderColorImage,
                            getPixelData: () => new Uint8Array(imageData.data.buffer),
                            rows: canvas.height,
                            columns: canvas.width,
                            height: canvas.height,
                            width: canvas.width,
                            color: true,
                            columnPixelSpacing: 1,
                            rowPixelSpacing: 1,
                            sizeInBytes: imageData.data.length
                        };
                        
                        // Register a custom image loader for video frames
                        if (!window.videoImageLoader) {
                            window.videoImageLoader = {};
                            
                            // Register the video image loader with Cornerstone3D
                            imageLoader.registerImageLoader('video', (imageId) => {
                                console.log('Video image loader called for:', imageId);
                                
                                return new Promise((resolve, reject) => {
                                    // Use setTimeout to make this async and avoid synchronous resolution issues
                                    setTimeout(() => {
                                        try {
                                            const cachedImage = window.videoImageLoader[imageId];
                                            if (cachedImage) {
                                                console.log('Found cached video image:', imageId);
                                                
                                                // Create proper Cornerstone3D image object
                                                const image = {
                                                    imageId: cachedImage.imageId,
                                                    minPixelValue: cachedImage.minPixelValue,
                                                    maxPixelValue: cachedImage.maxPixelValue,
                                                    slope: cachedImage.slope,
                                                    intercept: cachedImage.intercept,
                                                    windowCenter: cachedImage.windowCenter,
                                                    windowWidth: cachedImage.windowWidth,
                                                    rows: cachedImage.rows,
                                                    columns: cachedImage.columns,
                                                    height: cachedImage.height,
                                                    width: cachedImage.width,
                                                    color: cachedImage.color,
                                                    rgba: cachedImage.rgba,
                                                    columnPixelSpacing: cachedImage.columnPixelSpacing,
                                                    rowPixelSpacing: cachedImage.rowPixelSpacing,
                                                    sizeInBytes: cachedImage.sizeInBytes,
                                                    getPixelData: cachedImage.getPixelData,
                                                    // Add required properties for Cornerstone3D
                                                    photometricInterpretation: 'RGB',
                                                    samplesPerPixel: 4,
                                                    planarConfiguration: 0,
                                                    pixelRepresentation: 0,
                                                    bitsAllocated: 8,
                                                    bitsStored: 8,
                                                    highBit: 7,
                                                    voiLut: undefined,
                                                    modalityLut: undefined,
                                                    colormap: undefined,
                                                    // Add scaling parameters
                                                    scalarData: cachedImage.getPixelData(),
                                                    spacing: [cachedImage.columnPixelSpacing, cachedImage.rowPixelSpacing, 1],
                                                    origin: [0, 0, 0],
                                                    direction: [1, 0, 0, 0, 1, 0, 0, 0, 1],
                                                    dimensions: [cachedImage.width, cachedImage.height, 1]
                                                };
                                                
                                                resolve(image);
                                            } else {
                                                console.warn('No cached image found for:', imageId);
                                                reject(new Error(`No cached image found for ${imageId}`));
                                            }
                                        } catch (error) {
                                            console.error('Error in video image loader:', error);
                                            reject(error);
                                        }
                                    }, 0);
                                });
                            });
                        }
                        
                        // Cache the synthetic image for the image loader
                        window.videoImageLoader[syntheticImageId] = {
                            imageId: syntheticImageId,
                            minPixelValue: 0,
                            maxPixelValue: 255,
                            slope: 1,
                            intercept: 0,
                            windowCenter: 128,
                            windowWidth: 256,
                            rows: canvas.height,
                            columns: canvas.width,
                            height: canvas.height,
                            width: canvas.width,
                            color: true,
                            columnPixelSpacing: 1,
                            rowPixelSpacing: 1,
                            sizeInBytes: imageData.data.length,
                            getPixelData: () => new Uint8Array(imageData.data.buffer),
                            rgba: true
                        };
                        
                        // Display the synthetic image with cornerstone-core
                        cornerstone.displayImage(element, syntheticImage);
                        console.log('Video frame captured and displayed for measurements');
                    }
                } catch (error) {
                    console.error('Error setting up video for measurements:', error);
                    return;
                }
            }
            
            // Set viewport ID early for use throughout the function
            const viewportId = element.id || 'defaultViewport';
            element.id = viewportId; // Ensure element has an ID

            // Check if element is enabled with cornerstone-core
            let enabledElement;
            try {
                enabledElement = cornerstone.getEnabledElement(element);
            } catch (e) {
                console.log('Element not enabled with cornerstone-core, skipping tool group setup');
                return;
            }

            // For MP4 video measurements, use simple approach - no complex VIDEO viewport
            if (fileInfo?.isMP4) {
                // Check if we already have a video tool group set up
                if (toolGroupRef.current && toolGroupRef.current.isVideo) {
                    console.log('MP4 video tool group already set up, skipping');
                    return;
                }
                
                // Store a simple reference to indicate video mode
                toolGroupRef.current = { isVideo: true, element };
                console.log('MP4 video tool group setup complete (simple mode)');
                return;
            }

            // Remove existing tool group if any (but not video tool groups)
            if (toolGroupRef.current && !toolGroupRef.current.isVideo) {
                try {
                    ToolGroupManager.destroyToolGroup(toolGroupId);
                } catch (e) {
                    console.log('Tool group already destroyed or not found');
                }
                toolGroupRef.current = null;
            }

            // Don't create Cornerstone3D tool groups for video content
            if (fileInfo?.isMP4) {
                console.log('Skipping Cornerstone3D tool group creation for MP4 video');
                return;
            }

            // Create new tool group for non-video content only
            let toolGroup;
            try {
                // First try to get existing tool group
                toolGroup = ToolGroupManager.getToolGroup(toolGroupId);
                if (toolGroup) {
                    console.log('Using existing tool group:', toolGroupId);
                } else {
                    // Create new tool group if it doesn't exist
                    toolGroup = ToolGroupManager.createToolGroup(toolGroupId);
                    console.log('Created new tool group:', toolGroupId);
                }
            } catch (e) {
                if (e.message.includes('already exists')) {
                    // Tool group exists, get it instead
                    try {
                        toolGroup = ToolGroupManager.getToolGroup(toolGroupId);
                        if (!toolGroup) {
                            ToolGroupManager.destroyToolGroup(toolGroupId);
                            toolGroup = ToolGroupManager.createToolGroup(toolGroupId);
                        }
                    } catch (e2) {
                        console.error('Error handling existing tool group:', e2);
                        return;
                    }
                } else {
                    console.error('Error creating tool group:', e);
                    return;
                }
            }
            
            if (!toolGroup) {
                console.error('Failed to create tool group');
                return;
            }
            
            toolGroupRef.current = toolGroup;

            // Only add tools if this is a newly created tool group
            const toolsToAdd = [
                { tool: WindowLevelTool.toolName, name: 'WindowLevel' },
                { tool: PanTool.toolName, name: 'Pan' },
                { tool: ZoomTool.toolName, name: 'Zoom' },
                { tool: LengthTool.toolName, name: 'Length' },
                { tool: EllipticalROITool.toolName, name: 'EllipticalROI' },
                { tool: RectangleROITool.toolName, name: 'RectangleROI' },
                { tool: AngleTool.toolName, name: 'Angle' },
                { tool: ProbeTool.toolName, name: 'Probe' },
                { tool: BidirectionalTool.toolName, name: 'Bidirectional' }
            ];

            // Check if tools are already added to avoid duplicate registration
            const existingTools = [];
            try {
                // Try to get existing tool names, but handle the case where the method doesn't exist
                if (typeof toolGroup.getToolNames === 'function') {
                    existingTools.push(...toolGroup.getToolNames());
                }
            } catch (e) {
                // Method might not exist or might fail, continue with empty array
            }
            
            toolsToAdd.forEach(({ tool, name }) => {
                if (!existingTools.includes(tool)) {
                    try {
                        toolGroup.addTool(tool);
                        console.log(`Added tool ${name} to tool group`);
                    } catch (e) {
                        if (!e.message.includes('already registered')) {
                            console.error(`Error adding tool ${name}:`, e);
                        }
                    }
                }
            });

            // Set initial active tool (safe to call multiple times)
            try {
                toolGroup.setToolActive(WindowLevelTool.toolName, {
                    bindings: [{ mouseButton: ToolEnums.MouseBindings.Primary }]
                });

                // Set other tools as passive (safe to call multiple times)
                toolGroup.setToolPassive(LengthTool.toolName);
                toolGroup.setToolPassive(EllipticalROITool.toolName);
                toolGroup.setToolPassive(AngleTool.toolName);
                toolGroup.setToolPassive(ProbeTool.toolName);
                toolGroup.setToolPassive(BidirectionalTool.toolName);
            } catch (e) {
                console.error('Error setting up tool states:', e);
            }

            // Add viewport to tool group - check if already added
            try {
                const existingViewports = [];
                try {
                    if (typeof toolGroup.getViewportIds === 'function') {
                        existingViewports.push(...toolGroup.getViewportIds());
                    }
                } catch (e) {
                    // Method might not exist, continue with empty array
                }
                
                if (!existingViewports.includes(viewportId)) {
                    toolGroup.addViewport(viewportId, 'cornerstone');
                    console.log(`Added viewport ${viewportId} to tool group`);
                }
            } catch (e) {
                console.error('Error adding viewport to tool group:', e);
            }
            console.log('Tool group setup complete for viewport:', viewportId);
        } catch (error) {
            console.error('Error setting up tool group:', error);
        }
    }, [fileInfo?.isMP4, fileInfo?.isAHI, isPlaying, toolGroupId, fileName]);

    // Handle measurement completion
    const handleMeasurementComplete = useCallback((measurementId, value, unit) => {
        console.log('Measurement complete:', measurementId, value, unit);
        setMeasurementResults(prev => {
            const updated = {
                ...prev,
                [measurementId]: { value, unit, timestamp: new Date().toISOString() }
            };
            console.log('Updated measurement results:', updated);
            return updated;
        });
    }, []);

    // Handle tool selection
    const selectTool = useCallback(async (toolName) => {
        setActiveTool(toolName);

        // Allow tool selection for MP4 files when paused
        if (!toolGroupRef.current || (fileInfo?.isMP4 && isPlaying)) return;

        try {
            const toolGroup = toolGroupRef.current;

            // Check if this is an AHI image (using Cornerstone3D)
            if (fileInfo?.isAHI && !fileInfo?.isMP4) {
                console.log('Selecting tool for AHI single frame image:', toolName);
                console.log('Current tool group:', toolGroup);
                
                // For AHI images, toolGroup should be the actual Cornerstone3D tool group
                if (toolGroup && typeof toolGroup.setToolActive === 'function' && !toolGroup.isVideo) {
                    // Use AHI tool activation for Cornerstone3D
                    const success = activateAHIMeasurementTool(toolGroup, toolName);
                    if (success) {
                        console.log(`Activated AHI tool: ${toolName}`);
                        
                        // Add visual feedback for measurement mode
                        const viewportElement = viewportRef.current;
                        if (viewportElement && toolName !== 'WindowLevel') {
                            viewportElement.classList.add('measurement-mode');
                        } else if (viewportElement) {
                            viewportElement.classList.remove('measurement-mode');
                        }
                    }
                } else {
                    console.error('Invalid tool group for AHI image:', toolGroup);
                    console.error('Tool group type check - isVideo:', toolGroup?.isVideo, 'hasSetToolActive:', typeof toolGroup?.setToolActive);
                    
                    // If we have a video tool group but this is an AHI single frame, we need to wait for proper setup
                    if (toolGroup?.isVideo) {
                        console.log('AHI image has video tool group - waiting for proper AHI tool group setup');
                        return;
                    }
                }
                return;
            }

            // For video measurements, skip complex Cornerstone3D setup and use simple approach
            if (fileInfo?.isMP4 && toolGroup.isVideo) {
                console.log('Activating video measurement tool (simple mode):', toolName);
                
                const viewportElement = viewportRef.current;
                
                // Add visual feedback for measurement mode
                if (viewportElement && toolName !== 'WindowLevel') {
                    viewportElement.classList.add('measurement-mode');
                    console.log('Video measurement mode activated with visual feedback');
                } else if (viewportElement) {
                    viewportElement.classList.remove('measurement-mode');
                }
                
                // Store simple tool reference
                toolGroupRef.current = { 
                    isVideo: true, 
                    element: viewportElement, 
                    activeTool: toolName,
                    // Add a simple measurement handler
                    handleMeasurement: (startPoint, endPoint) => {
                        console.log('Video measurement captured:', { startPoint, endPoint, tool: toolName });
                        
                        // Calculate simple distance for length measurements
                        if (toolName === 'length') {
                            const distance = Math.sqrt(
                                Math.pow(endPoint.x - startPoint.x, 2) + 
                                Math.pow(endPoint.y - startPoint.y, 2)
                            );
                            
                            // Convert to real-world units if possible
                            // For now, just use pixel distance
                            const pixelDistance = distance;
                            
                            // Trigger measurement completion
                            if (activeMeasurementType) {
                                handleMeasurementComplete(
                                    activeMeasurementType.id, 
                                    parseFloat(pixelDistance.toFixed(2)), 
                                    'px'
                                );
                            }
                        }
                    }
                };
                
                console.log('Video measurement tool activated (simple mode)');
                return;
            }

            // Regular Cornerstone3D tool activation for non-video content
            // Only proceed if we have a proper Cornerstone3D tool group (not a video tool group)
            if (!toolGroup || toolGroup.isVideo || typeof toolGroup.setToolActive !== 'function') {
                console.log('No valid Cornerstone3D tool group available for tool selection');
                return;
            }

            const toolMapping = {
                'WindowLevel': WindowLevelTool.toolName,
                'Pan': PanTool.toolName,
                'Zoom': ZoomTool.toolName,
                'length': LengthTool.toolName,
                'area': EllipticalROITool.toolName,
                'ellipse': EllipticalROITool.toolName,
                'angle': AngleTool.toolName,
                'probe': ProbeTool.toolName,
                'bidirectional': BidirectionalTool.toolName
            };

            // Deactivate all tools - only if we have a proper tool group
            Object.values(toolMapping).forEach(csToolName => {
                try {
                    toolGroup.setToolPassive(csToolName);
                } catch (error) {
                    console.log(`Tool ${csToolName} not available in tool group:`, error.message);
                }
            });

            // Activate the selected tool
            const cornerstoneToolName = toolMapping[toolName];
            if (cornerstoneToolName) {
                try {
                    toolGroup.setToolActive(cornerstoneToolName, {
                        bindings: [{ mouseButton: ToolEnums.MouseBindings.Primary }]
                    });
                    console.log(`Activated tool: ${cornerstoneToolName}`);
                    
                    // Add visual feedback for measurement mode
                    const regularElement = viewportRef.current;
                    if (regularElement && toolName !== 'WindowLevel') {
                        regularElement.classList.add('measurement-mode');
                    } else if (regularElement) {
                        regularElement.classList.remove('measurement-mode');
                    }
                } catch (error) {
                    console.error(`Error activating tool ${cornerstoneToolName}:`, error);
                }
            }
        } catch (error) {
            console.error('Error selecting tool:', error);
        }
    }, [fileInfo?.isMP4, fileInfo?.isAHI, isPlaying, fileName, currentFrame, activeMeasurementType, handleMeasurementComplete]);

    // Handle measurement selection from panel
    const handleMeasurementSelection = useCallback((measurement) => {
        console.log('Measurement selected:', measurement);
        setActiveMeasurementType(measurement);

        // For video measurements, ensure tool group is set up first
        // But NEVER override an existing AHI tool group
        if (fileInfo?.isMP4 && !isPlaying) {
            // Only set up video tool group if we don't have any tool group OR if we have a video tool group
            // Never override an AHI tool group
            if (!toolGroupRef.current || (toolGroupRef.current.isVideo && !toolGroupRef.current._isAHIToolGroup)) {
                // Additional check: if this is an AHI single frame, don't set up video tool group
                if (fileInfo?.isAHI && !fileInfo?.isMP4) {
                    console.log('Skipping video tool group setup for AHI single frame image');
                } else {
                    const viewportElement = viewportRef.current;
                    if (viewportElement) {
                        toolGroupRef.current = { 
                            isVideo: true, 
                            element: viewportElement,
                            activeTool: null
                        };
                    }
                }
            }
        }

        // Activate the appropriate tool
        if (measurement.tool) {
            selectTool(measurement.tool);
        }
    }, [selectTool, fileInfo?.isMP4, fileInfo?.isAHI, isPlaying]);

    // Listen for annotation completed events
    useEffect(() => {
        if (!viewportRef.current || (fileInfo?.isMP4 && isPlaying)) return;

        const element = viewportRef.current;

        const handleAnnotationCompleted = (evt) => {
            console.log('Annotation completed event:', evt);
            console.log('Event detail:', evt.detail);
            console.log('Active measurement type:', activeMeasurementType);

            if (evt.detail && activeMeasurementType) {
                const { annotation: completedAnnotation } = evt.detail;
                console.log('Completed annotation:', completedAnnotation);

                if (completedAnnotation && completedAnnotation.data) {
                    // Get the current image ID for pixel spacing extraction
                    let currentImageId = preloadedImageId;
                    if (fileInfo?.isAHI && fileInfo?.isMP4) {
                        currentImageId = `ahi:video/${fileName}`;
                    } else if (fileInfo?.isAHI) {
                        currentImageId = preloadedImageId;
                    }

                    console.log('Using image ID for measurement processing:', currentImageId);

                    // Use MeasurementProcessor to get properly converted values
                    let result = null;
                    
                    switch (activeMeasurementType.tool) {
                        case MEASUREMENT_TOOLS.LENGTH:
                            console.log('Processing length measurement...');
                            result = MeasurementProcessor.processLengthMeasurement(completedAnnotation, currentImageId);
                            break;
                        case MEASUREMENT_TOOLS.AREA:
                        case MEASUREMENT_TOOLS.ELLIPSE:
                            console.log('Processing area measurement...');
                            result = MeasurementProcessor.processAreaMeasurement(completedAnnotation, currentImageId);
                            break;
                        case MEASUREMENT_TOOLS.ANGLE:
                            console.log('Processing angle measurement...');
                            result = MeasurementProcessor.processAngleMeasurement(completedAnnotation, currentImageId);
                            break;
                        case MEASUREMENT_TOOLS.VELOCITY:
                            console.log('Processing velocity measurement...');
                            result = MeasurementProcessor.processVelocityMeasurement(completedAnnotation, currentImageId);
                            break;
                        default:
                            console.log('Unknown measurement tool type:', activeMeasurementType.tool);
                            break;
                    }

                    console.log('Measurement processing result:', result);

                    if (result && result.value !== null && result.value !== undefined) {
                        console.log('Processed measurement result:', result);
                        handleMeasurementComplete(activeMeasurementType.id, parseFloat(result.value.toFixed(2)), result.unit);
                    } else {
                        console.warn('No valid measurement result obtained');
                        // Fallback to raw annotation data if processing fails
                        const stats = completedAnnotation.data.cachedStats || {};
                        let value = null;
                        let unit = activeMeasurementType.unit;

                        switch (activeMeasurementType.tool) {
                            case MEASUREMENT_TOOLS.LENGTH:
                                value = stats.length;
                                break;
                            case MEASUREMENT_TOOLS.AREA:
                            case MEASUREMENT_TOOLS.ELLIPSE:
                                value = stats.area;
                                break;
                            case MEASUREMENT_TOOLS.ANGLE:
                                value = stats.angle;
                                unit = '°';
                                break;
                            default:
                                break;
                        }

                        if (value !== null && value !== undefined) {
                            console.log('Using fallback measurement value:', value, unit);
                            handleMeasurementComplete(activeMeasurementType.id, parseFloat(value.toFixed(2)), unit);
                        }
                    }
                }
            }
        };

        // Try different event types for compatibility
        element.addEventListener('cornerstoneannotationcompleted', handleAnnotationCompleted);
        element.addEventListener('cornerstonetoolsmeasurementadded', handleAnnotationCompleted);
        element.addEventListener('cornerstonetoolsmeasurementmodified', handleAnnotationCompleted);
        
        // Also try Cornerstone3D events
        element.addEventListener('ANNOTATION_COMPLETED', handleAnnotationCompleted);
        element.addEventListener('ANNOTATION_MODIFIED', handleAnnotationCompleted);
        
        // Add more Cornerstone3D event types
        element.addEventListener('CORNERSTONE_TOOLS_MEASUREMENT_ADDED', handleAnnotationCompleted);
        element.addEventListener('CORNERSTONE_TOOLS_MEASUREMENT_COMPLETED', handleAnnotationCompleted);
        
        // Debug: Log all events on the element
        const debugEventHandler = (evt) => {
            if (evt.type.toLowerCase().includes('annotation') || 
                evt.type.toLowerCase().includes('measurement') ||
                evt.type.toLowerCase().includes('tool')) {
                console.log('Debug: Event fired:', evt.type, evt);
            }
        };
        
        // Add debug listener for all events
        element.addEventListener('*', debugEventHandler, true);

        return () => {
            element.removeEventListener('cornerstoneannotationcompleted', handleAnnotationCompleted);
            element.removeEventListener('cornerstonetoolsmeasurementadded', handleAnnotationCompleted);
            element.removeEventListener('cornerstonetoolsmeasurementmodified', handleAnnotationCompleted);
            element.removeEventListener('ANNOTATION_COMPLETED', handleAnnotationCompleted);
            element.removeEventListener('ANNOTATION_MODIFIED', handleAnnotationCompleted);
            element.removeEventListener('CORNERSTONE_TOOLS_MEASUREMENT_ADDED', handleAnnotationCompleted);
            element.removeEventListener('CORNERSTONE_TOOLS_MEASUREMENT_COMPLETED', handleAnnotationCompleted);
        };
    }, [activeMeasurementType, handleMeasurementComplete, fileInfo?.isMP4, isPlaying, preloadedImageId, fileName, fileInfo?.isAHI]);

    // Sync annotation state with parent props
    useEffect(() => {
        if (parentAnnotationMode !== undefined && parentAnnotationMode !== annotationMode) {
            setAnnotationMode(parentAnnotationMode);
            if (onAnnotationModeChange) {
                onAnnotationModeChange(parentAnnotationMode);
            }
        }
    }, [parentAnnotationMode, annotationMode, onAnnotationModeChange]);

    useEffect(() => {
        if (parentActiveTool !== undefined && parentActiveTool !== activeTool) {
            setActiveTool(parentActiveTool);
            if (onActiveToolChange) {
                onActiveToolChange(parentActiveTool);
            }
            // Also call selectTool to actually activate the tool
            selectTool(parentActiveTool);
        }
    }, [parentActiveTool, activeTool, onActiveToolChange, selectTool]);

    useEffect(() => {
        if (parentCurrentColor !== undefined && parentCurrentColor !== currentColor) {
            setCurrentColor(parentCurrentColor);
        }
    }, [parentCurrentColor, currentColor]);

    useEffect(() => {
        if (parentCurrentLineWidth !== undefined && parentCurrentLineWidth !== currentLineWidth) {
            setCurrentLineWidth(parentCurrentLineWidth);
        }
    }, [parentCurrentLineWidth, currentLineWidth]);

    // Toggle annotation mode
    const toggleAnnotationMode = () => {
        const newMode = !annotationMode;
        setAnnotationMode(newMode);
        if (onAnnotationModeChange) {
            onAnnotationModeChange(newMode);
        }
        
        if (newMode) {
            // Entering annotation mode - activate pencil tool
            const newTool = 'pencil';
            setActiveTool(newTool);
            if (onActiveToolChange) {
                onActiveToolChange(newTool);
            }
            selectTool(newTool);
        } else {
            // Exiting annotation mode - return to window/level
            const newTool = 'WindowLevel';
            setActiveTool(newTool);
            if (onActiveToolChange) {
                onActiveToolChange(newTool);
            }
            selectTool(newTool);
        }
    };

    // Clear all annotations
    const clearAnnotations = () => {
        const sopId = currentSopInstanceUID || 'default';
        
        if (fileInfo && fileInfo.numberOfFrames > 1) {
            // In multiframe mode, only clear annotations for current frame
            setAnnotations(prev => ({
                ...prev,
                [sopId]: (prev[sopId] || []).filter(ann => ann.frameNumber !== currentFrame)
            }));
        } else {
            // Clear all annotations for current SOP
            setAnnotations(prev => ({
                ...prev,
                [sopId]: []
            }));
        }
        
        // Clear the canvas for current SOP
        const canvas = canvasesRef.current[sopId] || annotationCanvasRef.current;
        if (canvas) {
            const ctx = canvas.getContext('2d');
            ctx.clearRect(0, 0, canvas.width, canvas.height);
        }
    };

    // Handle annotation drawing
    const startDrawing = (e) => {
        // Check if we're in video measurement mode
        if (fileInfo?.isMP4 && !isPlaying && toolGroupRef.current?.isVideo && activeMeasurementType && activeTool !== 'WindowLevel') {
            console.log('Starting video measurement');
            const viewport = viewportRef.current;
            if (!viewport) return;

            const rect = viewport.getBoundingClientRect();
            const startPoint = {
                x: e.clientX - rect.left,
                y: e.clientY - rect.top
            };

            // Store start point for measurement
            viewport.measurementStart = startPoint;
            setIsDrawing(true);
            return;
        }

        if (!annotationMode) return;

        const sopId = currentSopInstanceUID || 'default';
        const canvas = canvasesRef.current[sopId] || annotationCanvasRef.current;
        const viewport = viewportRef.current;
        if (!canvas || !viewport) return;

        // Get the canvas bounds
        const canvasRect = canvas.getBoundingClientRect();

        // Calculate position relative to the canvas
        const x = e.clientX - canvasRect.left;
        const y = e.clientY - canvasRect.top;

        // Store the relative position (0-1 range) for proper scaling
        const relativeX = x / canvas.width;
        const relativeY = y / canvas.height;

        setIsDrawing(true);

        // Handle different annotation tools
        if (activeTool === 'pencil') {
            // Pencil tool - freehand drawing
            setAnnotations(prev => ({
                ...prev,
                [sopId]: [...(prev[sopId] || []), {
                    type: 'path',
                    points: [{ x: relativeX, y: relativeY }],
                    color: currentColor,
                    lineWidth: currentLineWidth,
                    frameNumber: currentFrame
                }]
            }));
        } else if (activeTool === 'arrow') {
            // Arrow tool - start point for arrow
            setAnnotations(prev => ({
                ...prev,
                [sopId]: [...(prev[sopId] || []), {
                    type: 'arrow',
                    startPoint: { x: relativeX, y: relativeY },
                    endPoint: { x: relativeX, y: relativeY }, // Will be updated on mouse move
                    color: currentColor,
                    lineWidth: currentLineWidth,
                    frameNumber: currentFrame
                }]
            }));
        } else if (activeTool === 'text') {
            // Text tool - prompt for text input
            const text = prompt('Enter text annotation:');
            if (text && text.trim()) {
                setAnnotations(prev => ({
                    ...prev,
                    [sopId]: [...(prev[sopId] || []), {
                        type: 'text',
                        position: { x: relativeX, y: relativeY },
                        text: text.trim(),
                        color: currentColor,
                        fontSize: Math.max(12, currentLineWidth * 6), // Scale font size with line width
                        frameNumber: currentFrame
                    }]
                }));
                redrawAnnotations();
            }
            setIsDrawing(false); // Text doesn't need dragging
            return;
        }
    };

    const draw = (e) => {
        if (!isDrawing || !annotationMode) return;

        const sopId = currentSopInstanceUID || 'default';
        const canvas = canvasesRef.current[sopId] || annotationCanvasRef.current;
        if (!canvas) return;

        const canvasRect = canvas.getBoundingClientRect();
        const x = e.clientX - canvasRect.left;
        const y = e.clientY - canvasRect.top;

        // Store the relative position (0-1 range)
        const relativeX = x / canvas.width;
        const relativeY = y / canvas.height;

        if (activeTool === 'pencil') {
            // Pencil tool - add points to path
            setAnnotations(prev => {
                const sopAnnotations = [...(prev[sopId] || [])];
                const currentPath = sopAnnotations[sopAnnotations.length - 1];
                if (currentPath && currentPath.type === 'path') {
                    currentPath.points.push({ x: relativeX, y: relativeY });
                }
                return {
                    ...prev,
                    [sopId]: sopAnnotations
                };
            });
        } else if (activeTool === 'arrow') {
            // Arrow tool - update end point
            setAnnotations(prev => {
                const sopAnnotations = [...(prev[sopId] || [])];
                const currentArrow = sopAnnotations[sopAnnotations.length - 1];
                if (currentArrow && currentArrow.type === 'arrow') {
                    currentArrow.endPoint = { x: relativeX, y: relativeY };
                }
                return {
                    ...prev,
                    [sopId]: sopAnnotations
                };
            });
        }

        // Redraw canvas
        redrawAnnotations();
    };

    const stopDrawing = (e) => {
        // Check if we're completing a video measurement
        if (fileInfo?.isMP4 && !isPlaying && toolGroupRef.current?.isVideo && activeMeasurementType && activeTool !== 'WindowLevel' && isDrawing) {
            console.log('Completing video measurement');
            const viewport = viewportRef.current;
            if (viewport && viewport.measurementStart) {
                const rect = viewport.getBoundingClientRect();
                const endPoint = {
                    x: e.clientX - rect.left,
                    y: e.clientY - rect.top
                };

                // Call the measurement handler
                if (toolGroupRef.current.handleMeasurement) {
                    toolGroupRef.current.handleMeasurement(viewport.measurementStart, endPoint);
                }

                // Clean up
                delete viewport.measurementStart;
            }
        }

        setIsDrawing(false);
    };

    // Redraw all annotations
    const redrawAnnotations = () => {
        const sopId = currentSopInstanceUID || 'default';
        const canvas = canvasesRef.current[sopId] || annotationCanvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Don't draw annotations if they're hidden
        if (!showAnnotations) return;

        // Get annotations for current SOP
        const sopAnnotations = annotations[sopId] || [];
        
        // Only draw annotations for the current frame in multiframe mode
        const relevantAnnotations = fileInfo && fileInfo.numberOfFrames > 1
            ? sopAnnotations.filter(ann => ann.frameNumber === currentFrame)
            : sopAnnotations;

        relevantAnnotations.forEach(annotation => {
            ctx.strokeStyle = annotation.color;
            ctx.fillStyle = annotation.color;
            ctx.lineWidth = annotation.lineWidth;
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';

            if (annotation.type === 'path' && annotation.points.length > 0) {
                // Pencil tool - freehand path
                ctx.beginPath();
                
                // Convert relative positions back to absolute positions
                const firstPoint = annotation.points[0];
                ctx.moveTo(firstPoint.x * canvas.width, firstPoint.y * canvas.height);

                for (let i = 1; i < annotation.points.length; i++) {
                    const point = annotation.points[i];
                    ctx.lineTo(point.x * canvas.width, point.y * canvas.height);
                }
                ctx.stroke();
            } else if (annotation.type === 'arrow' && annotation.startPoint && annotation.endPoint) {
                // Arrow tool - draw arrow line with arrowhead
                const startX = annotation.startPoint.x * canvas.width;
                const startY = annotation.startPoint.y * canvas.height;
                const endX = annotation.endPoint.x * canvas.width;
                const endY = annotation.endPoint.y * canvas.height;

                // Draw arrow line
                ctx.beginPath();
                ctx.moveTo(startX, startY);
                ctx.lineTo(endX, endY);
                ctx.stroke();

                // Draw arrowhead
                const angle = Math.atan2(endY - startY, endX - startX);
                const arrowLength = Math.max(10, annotation.lineWidth * 5);
                const arrowAngle = Math.PI / 6; // 30 degrees

                ctx.beginPath();
                ctx.moveTo(endX, endY);
                ctx.lineTo(
                    endX - arrowLength * Math.cos(angle - arrowAngle),
                    endY - arrowLength * Math.sin(angle - arrowAngle)
                );
                ctx.moveTo(endX, endY);
                ctx.lineTo(
                    endX - arrowLength * Math.cos(angle + arrowAngle),
                    endY - arrowLength * Math.sin(angle + arrowAngle)
                );
                ctx.stroke();
            } else if (annotation.type === 'text' && annotation.text && annotation.position) {
                // Text tool - draw text
                const x = annotation.position.x * canvas.width;
                const y = annotation.position.y * canvas.height;

                ctx.font = `${annotation.fontSize || 16}px Arial`;
                ctx.textAlign = 'left';
                ctx.textBaseline = 'top';

                // Draw text background for better readability
                const textMetrics = ctx.measureText(annotation.text);
                const textWidth = textMetrics.width;
                const textHeight = annotation.fontSize || 16;
                
                ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
                ctx.fillRect(x - 2, y - 2, textWidth + 4, textHeight + 4);

                // Draw text
                ctx.fillStyle = annotation.color;
                ctx.fillText(annotation.text, x, y);
            }
        });
    };

    // Setup tool group when video is paused
    useEffect(() => {
        if (fileInfo?.isMP4 && !isPlaying && viewportRef.current) {
            // Small delay to ensure video frame is stable
            const timer = setTimeout(() => {
                setupToolGroup();
            }, 100);

            return () => clearTimeout(timer);
        }
    }, [fileInfo?.isMP4, isPlaying, setupToolGroup]);

    // Add mouse event listeners for video measurements
    useEffect(() => {
        const viewport = viewportRef.current;
        const canvas = annotationCanvasRef.current;
        if (!viewport || !canvas) return;

        const handleVideoMeasurementMouseDown = (e) => {
            // Only handle if we're in video measurement mode
            if (fileInfo?.isMP4 && !isPlaying && toolGroupRef.current?.isVideo && activeMeasurementType && activeTool !== 'WindowLevel') {
                console.log('Video measurement mouse down detected');
                e.preventDefault();
                e.stopPropagation();
                
                const rect = viewport.getBoundingClientRect();
                const startPoint = {
                    x: e.clientX - rect.left,
                    y: e.clientY - rect.top
                };

                // Store start point for measurement
                viewport.measurementStart = startPoint;
                setIsDrawing(true);
                
                console.log('Video measurement started at:', startPoint);
            }
        };

        const handleVideoMeasurementMouseMove = (e) => {
            // Only handle if we're dragging a video measurement
            if (fileInfo?.isMP4 && !isPlaying && toolGroupRef.current?.isVideo && activeMeasurementType && activeTool !== 'WindowLevel' && isDrawing && viewport.measurementStart) {
                e.preventDefault();
                e.stopPropagation();
                
                const rect = viewport.getBoundingClientRect();
                const currentPoint = {
                    x: e.clientX - rect.left,
                    y: e.clientY - rect.top
                };

                // Draw temporary measurement line on canvas
                const ctx = canvas.getContext('2d');
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                
                // Draw measurement line
                ctx.beginPath();
                ctx.strokeStyle = '#00ff00'; // Green color
                ctx.lineWidth = 2;
                ctx.setLineDash([5, 5]); // Dashed line
                ctx.moveTo(viewport.measurementStart.x, viewport.measurementStart.y);
                ctx.lineTo(currentPoint.x, currentPoint.y);
                ctx.stroke();
                
                // Draw start and end points
                ctx.setLineDash([]); // Solid line for points
                ctx.fillStyle = '#00ff00';
                ctx.beginPath();
                ctx.arc(viewport.measurementStart.x, viewport.measurementStart.y, 3, 0, 2 * Math.PI);
                ctx.fill();
                ctx.beginPath();
                ctx.arc(currentPoint.x, currentPoint.y, 3, 0, 2 * Math.PI);
                ctx.fill();
            }
        };

        const handleVideoMeasurementMouseUp = (e) => {
            // Only handle if we're completing a video measurement
            if (fileInfo?.isMP4 && !isPlaying && toolGroupRef.current?.isVideo && activeMeasurementType && activeTool !== 'WindowLevel' && isDrawing) {
                console.log('Video measurement mouse up detected');
                e.preventDefault();
                e.stopPropagation();
                
                if (viewport.measurementStart) {
                    const rect = viewport.getBoundingClientRect();
                    const endPoint = {
                        x: e.clientX - rect.left,
                        y: e.clientY - rect.top
                    };

                    console.log('Video measurement completed:', { start: viewport.measurementStart, end: endPoint });

                    // Call the measurement handler
                    if (toolGroupRef.current.handleMeasurement) {
                        toolGroupRef.current.handleMeasurement(viewport.measurementStart, endPoint);
                    }

                    // Clear the temporary drawing
                    const ctx = canvas.getContext('2d');
                    ctx.clearRect(0, 0, canvas.width, canvas.height);

                    // Clean up
                    delete viewport.measurementStart;
                }
                
                setIsDrawing(false);
            }
        };

        // Add event listeners to the viewport
        viewport.addEventListener('mousedown', handleVideoMeasurementMouseDown, true);
        viewport.addEventListener('mousemove', handleVideoMeasurementMouseMove, true);
        viewport.addEventListener('mouseup', handleVideoMeasurementMouseUp, true);

        return () => {
            viewport.removeEventListener('mousedown', handleVideoMeasurementMouseDown, true);
            viewport.removeEventListener('mousemove', handleVideoMeasurementMouseMove, true);
            viewport.removeEventListener('mouseup', handleVideoMeasurementMouseUp, true);
        };
    }, [fileInfo?.isMP4, isPlaying, activeMeasurementType, activeTool, isDrawing]);

    useEffect(() => {
        const updateCanvasSize = () => {
            const viewport = viewportRef.current;
            const sopId = currentSopInstanceUID || 'default';
            
            // Update the current SOP's canvas
            let canvas = canvasesRef.current[sopId];
            if (!canvas) {
                // Create a new canvas for this SOP
                canvas = document.createElement('canvas');
                canvas.className = `annotation-canvas ${annotationMode ? 'active' : ''}`;
                canvas.style.position = 'absolute';
                canvas.style.top = '0';
                canvas.style.left = '0';
                canvas.style.pointerEvents = 'none';
                canvas.style.zIndex = '10';
                
                // Add event listeners for drawing
                canvas.onmousedown = startDrawing;
                canvas.onmousemove = draw;
                canvas.onmouseup = stopDrawing;
                canvas.onmouseleave = stopDrawing;
                canvas.style.pointerEvents = annotationMode ? 'auto' : 'none';
                
                canvasesRef.current[sopId] = canvas;
                
                // Add to viewport container
                if (viewport && viewport.parentElement) {
                    viewport.parentElement.appendChild(canvas);
                }
            }
            
            // Also update the main canvas for backward compatibility
            const mainCanvas = annotationCanvasRef.current;
            
            if (viewport && (canvas || mainCanvas)) {
                // Match the canvas size to the viewport
                const rect = viewport.getBoundingClientRect();
                
                if (canvas) {
                    canvas.width = rect.width;
                    canvas.height = rect.height;
                }
                
                if (mainCanvas) {
                    mainCanvas.width = rect.width;
                    mainCanvas.height = rect.height;
                }
                
                redrawAnnotations();
            }
        };

        updateCanvasSize();

        // Add a small delay to ensure proper sizing after drawer animations
        const timer1 = setTimeout(updateCanvasSize, 100);
        const timer2 = setTimeout(updateCanvasSize, 400);

        window.addEventListener('resize', updateCanvasSize);

        // Also trigger resize when drawer state changes
        if (viewportRef.current) {
            const element = viewportRef.current;
            try {
                const enabledElement = cornerstone.getEnabledElement(element);
                if (enabledElement) {
                    cornerstone.resize(element);
                }
            } catch (e) {
                // Element might not be enabled yet
            }
        }

        return () => {
            clearTimeout(timer1);
            clearTimeout(timer2);
            window.removeEventListener('resize', updateCanvasSize);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rightDrawerOpen, currentSopInstanceUID, annotationMode]);

    // Redraw when annotations or current frame changes
    useEffect(() => {
        redrawAnnotations();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [annotations, currentFrame]);

    // Reset viewport
    const resetViewport = () => {
        const element = viewportRef.current;
        if (element) {
            try {
                const enabledElement = cornerstone.getEnabledElement(element);
                if (enabledElement) {
                    cornerstone.reset(element);
                }
            } catch (error) {
                console.debug('Element not enabled for reset');
            }
        }
    };

    // Invert image
    const invertImage = () => {
        const element = viewportRef.current;
        if (element) {
            try {
                const enabledElement = cornerstone.getEnabledElement(element);
                if (enabledElement) {
                    const viewport = cornerstone.getViewport(element);
                    viewport.invert = !viewport.invert;
                    cornerstone.setViewport(element, viewport);
                }
            } catch (error) {
                console.debug('Element not enabled for invert');
            }
        }
    };

    // Handle file selection
    const handleFileSelect = useCallback(async (event) => {
        let file;
        let imageId;

        // Handle different input types
        if (event && event.target && event.target.files) {
            if (!event.target.files.length || !isInitialized) return;
            file = event.target.files[0];
        } else if (event instanceof File) {
            file = event;
        } else if (typeof event === 'string' && event.startsWith('ahi:')) {
            // It's an AHI image ID
            imageId = event;
        } else {
            return;
        }

        setIsLoading(true);
        setError(null);
        setShowControls(false);
        setIsPlaying(false);

        // Clean up previous resources
        cleanupResources();

        try {
            if (imageId && isAHI) {
                // Handle AHI image ID
                console.log('Loading AHI image:', imageId);

                // For AHI images, check the transfer syntax to set proper flags
                const cachedData = window.ahiImageCache && window.ahiImageCache[imageId];
                const transferSyntaxUID = cachedData?.metadata?.TransferSyntaxUID || 
                                        cachedData?.metadata?.transferSyntaxUID;
                
                const isMP4TransferSyntax = transferSyntaxUID === '1.2.840.10008.1.2.4.102' || 
                                          transferSyntaxUID === '1.2.840.10008.1.2.4.103';
                
                setFileInfo({
                    filename: fileName || 'AHI Image',
                    isMP4: isMP4TransferSyntax,
                    isAHI: true,
                    numberOfFrames: isMP4TransferSyntax ? (cachedData?.metadata?.numberOfFrames || 40) : 1,
                    transferSyntaxUID: transferSyntaxUID
                });
                
                if (isMP4TransferSyntax) {
                    setTotalFrames(cachedData?.metadata?.numberOfFrames || 40);
                    setCurrentFrame(1);
                    setShowControls(true);
                }

                await displayWithStackViewport(imageId);
            } else if (file) {
                // Handle regular file
                console.log('Loading file:', file.name);

                // Check if this is an AHI video file (MP4 from AHI)
                if (isAHI && (file.type === 'video/mp4' || file.name.endsWith('.mp4'))) {
                    // This is a raw MP4 file from AHI, not a DICOM file
                    console.log('Loading AHI MP4 video file');

                    // Get the number of frames from metadata or file name
                    let numberOfFrames = 40; // Default for AHI videos
                    let dicomMetadata = null;

                    // Use ahiMetadata if available
                    if (ahiMetadata && ahiMetadata.numberOfFrames) {
                        numberOfFrames = ahiMetadata.numberOfFrames;
                        dicomMetadata = ahiMetadata.dicomMetadata;
                    } else {
                        // Try to get from file name
                        const match = file.name.match(/video_(\d+)/);
                        if (match) {
                            // The number in the filename might be the index, not frame count
                            // Keep the default unless we have better info
                        }
                    }

                    // Try to find cached DICOM metadata for this video
                    if (!dicomMetadata && window.ahiImageCache) {
                        // Look for any cached entry that might contain this video's metadata
                        for (const [cacheKey, cacheData] of Object.entries(window.ahiImageCache)) {
                            if (cacheKey.includes(fileName) || (cacheData.metadata && cacheData.metadata.TransferSyntaxUID === '1.2.840.10008.1.2.4.102')) {
                                dicomMetadata = cacheData.metadata;
                                console.log('Found cached DICOM metadata for video:', dicomMetadata);
                                break;
                            }
                        }
                    }

                    setFileInfo({
                        filename: file.name,
                        isMP4: true,
                        isAHI: true,
                        numberOfFrames: numberOfFrames,
                        dimensions: 'Video',
                        transferSyntaxDesc: 'MPEG-4 AVC/H.264',
                        dicomMetadata: dicomMetadata // Store the DICOM metadata
                    });

                    setTotalFrames(numberOfFrames);
                    setCurrentFrame(1);

                    // Display the MP4 directly
                    await displayAHIVideoViewport(file, dicomMetadata);
                    setShowControls(true);
                } else {
                    // Try to read as DICOM file
                    const dicomData = await readDICOMFile(file);

                    // Extract metadata
                    const transferSyntax = dicomData.string('x00020010');
                    const transferSyntaxDesc = getTransferSyntaxDescription(dicomData);
                    const sopInstanceUID = dicomData.string('x00080018');
                    const seriesDescription = dicomData.string('x0008103e') || dicomData.string('x0008103E') || '';

                    // Get number of frames
                    let numberOfFrames = 1;
                    try {
                        const framesString = dicomData.string('x00280008');
                        if (framesString) {
                            numberOfFrames = parseInt(framesString, 10);
                        }
                        if (isNaN(numberOfFrames) || numberOfFrames < 1) {
                            numberOfFrames = 1;
                        }
                    } catch (error) {
                        numberOfFrames = 1;
                    }

                    const rows = dicomData.uint16('x00280010');
                    const columns = dicomData.uint16('x00280011');
                    const isMP4 = isMP4TransferSyntax(dicomData);

                    // Get window/level values
                    const windowCenterValue = dicomData.string('x00281050');
                    const windowWidthValue = dicomData.string('x00281051');
                    if (windowCenterValue) setWindowCenter(parseFloat(windowCenterValue));
                    if (windowWidthValue) setWindowWidth(parseFloat(windowWidthValue));

                    setFileInfo({
                        filename: file.name,
                        transferSyntax,
                        transferSyntaxDesc,
                        sopInstanceUID,
                        seriesDescription,
                        numberOfFrames,
                        dimensions: `${columns} x ${rows}`,
                        isMP4,
                        isAHI: false
                    });

                    // Set the current SOP instance UID for annotations
                    setCurrentSopInstanceUID(sopInstanceUID || 'default');

                    setTotalFrames(numberOfFrames);
                    setCurrentFrame(1);

                    // Display the image using appropriate viewport
                    if (isMP4 && numberOfFrames > 1) {
                        // Use VIDEO viewport for MP4 multiframe
                        await displayWithVideoViewport(file, dicomData);
                        // Show controls for multiframe videos
                        setShowControls(true);
                    } else {
                        // Use STACK viewport for everything else
                        await displayWithStackViewport(file);
                    }
                }
            }

        } catch (error) {
            console.error('Error loading DICOM file:', error);
            setError(`Error loading file: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isInitialized, isAHI, fileName, ahiMetadata]);

    // Display using STACK viewport (standard DICOM files and AHI images)
    const displayWithStackViewport = async (fileOrImageId) => {
        try {
            const element = viewportRef.current;

            // Clear any existing content
            while (element.firstChild) {
                element.removeChild(element.firstChild);
            }

            // Clean up any existing tool group to prevent conflicts
            if (toolGroupRef.current) {
                try {
                    if (toolGroupRef.current.isVideo) {
                        // Clear the reference for video tool groups
                        console.log('Cleaning up video tool group during viewport switch');
                        toolGroupRef.current = null;
                    } else if (typeof toolGroupRef.current.destroy === 'function') {
                        // Destroy Cornerstone3D tool groups
                        console.log('Destroying Cornerstone3D tool group during viewport switch');
                        ToolGroupManager.destroyToolGroup(toolGroupId);
                        toolGroupRef.current = null;
                    } else {
                        // Handle any other tool group types
                        console.log('Clearing unknown tool group type during viewport switch');
                        toolGroupRef.current = null;
                    }
                } catch (e) {
                    console.log('Tool group cleanup during viewport switch:', e);
                    toolGroupRef.current = null;
                }
            }

            let imageId;

            // Check if this is an AHI image ID or a file
            if (typeof fileOrImageId === 'string' && fileOrImageId.startsWith('ahi:')) {
                // It's an AHI image ID - check transfer syntax to determine viewport type
                imageId = fileOrImageId;
                
                // Get cached metadata to check transfer syntax
                const cachedData = window.ahiImageCache && window.ahiImageCache[imageId];
                const transferSyntaxUID = cachedData?.metadata?.TransferSyntaxUID || 
                                        cachedData?.metadata?.transferSyntaxUID;
                
                console.log('AHI Transfer Syntax:', transferSyntaxUID);
                
                // Check if this is MP4 transfer syntax
                const isMP4TransferSyntax = transferSyntaxUID === '1.2.840.10008.1.2.4.102' || 
                                          transferSyntaxUID === '1.2.840.10008.1.2.4.103';
                
                if (isMP4TransferSyntax) {
                    console.log('AHI MP4 detected - extracting video data for video viewport');
                    // For MP4 AHI images, extract the MP4 data and use video element
                    const mp4Data = cachedData.pixelData;
                    const blob = new Blob([mp4Data], { type: 'video/mp4' });
                    const videoUrl = URL.createObjectURL(blob);
                    extractedVideoUrlRef.current = videoUrl;

                    // Clear viewport and create video element
                    while (element.firstChild) {
                        element.removeChild(element.firstChild);
                    }

                    const video = document.createElement('video');
                    video.src = videoUrl;
                    video.style.width = '100%';
                    video.style.height = '100%';
                    video.style.objectFit = 'contain';
                    video.style.backgroundColor = '#000';
                    video.controls = false;
                    video.loop = true;

                    videoElementRef.current = video;
                    element.appendChild(video);

                    // Set up event listeners
                    video.addEventListener('loadedmetadata', () => {
                        console.log('AHI MP4 Video metadata loaded');
                        video.play().then(() => {
                            console.log('AHI MP4 Video autoplay started');
                            setIsPlaying(true);
                        }).catch(() => {
                            console.log('Auto-play blocked, trying muted');
                            video.muted = true;
                            video.play().then(() => {
                                console.log('AHI MP4 Video autoplay started (muted)');
                                setIsPlaying(true);
                            }).catch(err => {
                                console.log('Auto-play failed:', err);
                            });
                        });
                    });

                    console.log('AHI MP4 video displayed with VIDEO element');
                } else {
                    console.log('AHI single frame detected - using Cornerstone3D');
                    // Use Cornerstone3D for single frame AHI images
                    try {
                        const result = await setupAHIViewport(element, imageId);
                        renderingEngineRef.current = result.renderingEngine;
                        
                        // Setup AHI tool group - ensure it's a proper Cornerstone3D tool group
                        const viewportId = element.id || 'ahiViewport';
                        element.id = viewportId; // Ensure element has the correct ID
                        
                        // Clear any existing tool group reference first
                        toolGroupRef.current = null;
                        
                        const toolGroup = setupAHIToolGroup(viewportId, toolGroupId);
                        
                        // Ensure we have a valid tool group before setting the reference
                        if (toolGroup && typeof toolGroup.setToolActive === 'function') {
                            toolGroupRef.current = toolGroup;
                            
                            // Mark this as an AHI setup to prevent video tool group override
                            toolGroup._isAHIToolGroup = true;
                            
                            console.log('AHI single frame viewport setup complete');
                            console.log('AHI tool group type:', typeof toolGroup?.setToolActive, 'isVideo:', toolGroup?.isVideo);
                            console.log('AHI tool group reference set successfully');
                        } else {
                            console.error('Failed to set up AHI tool group - invalid tool group returned');
                        }
                    } catch (error) {
                        console.error('Error setting up AHI viewport:', error);
                        throw error;
                    }
                }
                
                console.log('AHI viewport and tools setup complete');
                
            } else if (fileOrImageId instanceof File) {
                // It's a regular file from local upload - use cornerstone-core
                
                // Check if this is a multiframe file that needs special handling
                if (fileInfo && fileInfo.numberOfFrames > 1 && !fileInfo.isMP4) {
                    console.log(`Setting up multiframe DICOM playback for ${fileInfo.numberOfFrames} frames`);
                    await displayMultiframeStackViewport(fileOrImageId, element);
                } else {
                    // Single frame or MP4 file - use standard display
                    imageId = cornerstoneWADOImageLoader.wadouri.fileManager.add(fileOrImageId);
                    
                    // Enable element with cornerstone-core
                    cornerstone.enable(element);

                    // Load and display the image
                    const image = await cornerstone.loadImage(imageId);
                    cornerstone.displayImage(element, image);

                    // Enable mouse wheel scrolling for stack
                    element.addEventListener('wheel', handleMouseWheel);

                    // Enable window/level with mouse
                    element.addEventListener('mousedown', handleMouseDown);
                    element.addEventListener('mousemove', handleMouseMove);
                    element.addEventListener('mouseup', handleMouseUp);

                    console.log('Single frame image displayed with Cornerstone-core');

                    // Setup tool group after image is displayed
                    setTimeout(() => {
                        setupToolGroup();
                    }, 100);
                }
            } else {
                throw new Error('Invalid input: expected File or AHI image ID');
            }

        } catch (error) {
            console.error('Error displaying with STACK viewport:', error);
            throw error;
        }
    };

    // Display multiframe DICOM files using Cornerstone stack functionality
    const displayMultiframeStackViewport = async (file, element) => {
        try {
            // Create image IDs for each frame
            const baseImageId = cornerstoneWADOImageLoader.wadouri.fileManager.add(file);
            const imageIds = [];
            
            // Generate frame-specific image IDs
            for (let i = 0; i < fileInfo.numberOfFrames; i++) {
                const frameImageId = `${baseImageId}?frame=${i}`;
                imageIds.push(frameImageId);
            }

            console.log(`Created ${imageIds.length} frame image IDs for multiframe playback`);

            // Enable element with cornerstone-core
            cornerstone.enable(element);

            // Load and display the first frame
            const firstImage = await cornerstone.loadImage(imageIds[0]);
            cornerstone.displayImage(element, firstImage);

            // Store the image stack for playback
            element.imageIds = imageIds;
            element.currentImageIdIndex = 0;

            // Enable mouse wheel scrolling for frame navigation
            element.addEventListener('wheel', handleMultiframeMouseWheel);

            // Enable window/level with mouse
            element.addEventListener('mousedown', handleMouseDown);
            element.addEventListener('mousemove', handleMouseMove);
            element.addEventListener('mouseup', handleMouseUp);

            console.log('Multiframe DICOM displayed with Cornerstone-core stack');

            // Show controls for multiframe files
            setShowControls(true);

            // Setup tool group after image is displayed
            setTimeout(() => {
                setupToolGroup();
            }, 100);

        } catch (error) {
            console.error('Error setting up multiframe stack viewport:', error);
            throw error;
        }
    };

    // Handle mouse wheel for multiframe navigation
    const handleMultiframeMouseWheel = (e) => {
        e.preventDefault();
        const element = viewportRef.current;
        if (!element || !element.imageIds) return;

        // Navigate frames with mouse wheel
        if (e.deltaY < 0) {
            // Scroll up - previous frame
            navigateToFrame(currentFrame - 1);
        } else {
            // Scroll down - next frame
            navigateToFrame(currentFrame + 1);
        }
    };

    // Navigate to a specific frame in multiframe files
    const navigateToFrame = async (frameNumber) => {
        const element = viewportRef.current;
        if (!element || !element.imageIds || !fileInfo || fileInfo.numberOfFrames <= 1) return;

        // Clamp frame number to valid range
        let targetFrame = frameNumber;
        if (targetFrame < 1) targetFrame = fileInfo.numberOfFrames;
        if (targetFrame > fileInfo.numberOfFrames) targetFrame = 1;

        const imageIndex = targetFrame - 1;
        
        try {
            // Load and display the target frame
            const image = await cornerstone.loadImage(element.imageIds[imageIndex]);
            cornerstone.displayImage(element, image);
            
            // Update current frame state
            element.currentImageIdIndex = imageIndex;
            setCurrentFrame(targetFrame);
            
            console.log(`Navigated to frame ${targetFrame} of ${fileInfo.numberOfFrames}`);
        } catch (error) {
            console.error('Error navigating to frame:', error);
        }
    };

    // Mouse handlers for window/level
    let isMouseDown = false;
    let lastX = 0;
    let lastY = 0;

    const handleMouseDown = (e) => {
        if (annotationMode || activeMeasurementType || (fileInfo?.isMP4 && isPlaying)) return; // Don't handle window/level in annotation, measurement mode, or when video is playing
        if (e.button === 0 && activeTool === 'WindowLevel') { // Left mouse button
            isMouseDown = true;
            lastX = e.pageX;
            lastY = e.pageY;
            e.preventDefault();
        }
    };

    const handleMouseMove = (e) => {
        if (annotationMode || activeMeasurementType || (fileInfo?.isMP4 && isPlaying)) return; // Don't handle window/level in annotation, measurement mode, or when video is playing
        if (!isMouseDown || activeTool !== 'WindowLevel') return;

        const element = viewportRef.current;
        if (element) {
            try {
                const enabledElement = cornerstone.getEnabledElement(element);
                if (enabledElement) {
                    const deltaX = e.pageX - lastX;
                    const deltaY = e.pageY - lastY;

                    const viewport = cornerstone.getViewport(element);
                    viewport.voi.windowWidth += deltaX;
                    viewport.voi.windowCenter += deltaY;
                    cornerstone.setViewport(element, viewport);

                    lastX = e.pageX;
                    lastY = e.pageY;
                }
            } catch (error) {
                // Element not enabled, ignore
                console.debug('Element not enabled for mouse move');
            }
        }
    };

    const handleMouseUp = () => {
        isMouseDown = false;
    };

    const handleMouseWheel = (e) => {
        e.preventDefault();
        const element = viewportRef.current;
        if (element) {
            try {
                const enabledElement = cornerstone.getEnabledElement(element);
                if (enabledElement) {
                    const viewport = cornerstone.getViewport(element);
                    if (e.deltaY < 0) {
                        viewport.scale += 0.1;
                    } else {
                        viewport.scale -= 0.1;
                    }
                    viewport.scale = Math.max(0.1, Math.min(viewport.scale, 20));
                    cornerstone.setViewport(element, viewport);
                }
            } catch (error) {
                // Element not enabled, ignore
                console.debug('Element not enabled for mouse wheel');
            }
        }
    };

    // Display AHI video directly (raw MP4 from AWS HealthImaging)
    const displayAHIVideoViewport = async (file, dicomMetadata = null) => {
        try {
            // Skip video display if in grid mode (DicomGridViewer handles it)
            if (isGridMode) {
                console.log('Skipping AHI video display in grid mode');
                return;
            }

            // Create blob URL from the file
            const videoUrl = URL.createObjectURL(file);
            extractedVideoUrlRef.current = videoUrl;

            // Clear viewport
            const element = viewportRef.current;
            if (!element) {
                console.warn('Viewport element not available for AHI video display');
                return;
            }
            
            while (element.firstChild) {
                element.removeChild(element.firstChild);
            }

            // Create a video element
            const video = document.createElement('video');
            video.src = videoUrl;
            video.style.width = '100%';
            video.style.height = '100%';
            video.style.objectFit = 'contain';
            video.style.backgroundColor = '#000';
            video.controls = false;
            video.loop = true;

            videoElementRef.current = video;
            element.appendChild(video);

            // If we have DICOM metadata, cache it for measurement tools
            if (dicomMetadata && fileName) {
                // Create a synthetic AHI image ID for this video
                const videoImageId = `ahi:video/${fileName}`;
                
                // Initialize cache if not exists
                if (!window.ahiImageCache) {
                    window.ahiImageCache = {};
                }
                
                // Cache the DICOM metadata so measurement tools can access it
                window.ahiImageCache[videoImageId] = {
                    pixelData: null, // Videos don't have pixel data in the traditional sense
                    metadata: dicomMetadata
                };
                
                console.log('Cached DICOM metadata for AHI video:', videoImageId, dicomMetadata);
            }

            // Set up event listeners
            video.addEventListener('loadedmetadata', () => {
                console.log('AHI Video metadata loaded');
                // Auto-play on load
                video.play().then(() => {
                    console.log('AHI Video autoplay started');
                    setIsPlaying(true);
                }).catch(() => {
                    // Auto-play might be blocked, try muted
                    console.log('Auto-play blocked, trying muted');
                    video.muted = true;
                    video.play().then(() => {
                        console.log('AHI Video autoplay started (muted)');
                        setIsPlaying(true);
                    }).catch(err => {
                        console.log('Auto-play failed:', err);
                    });
                });
            });

            // Add play/pause event listeners to track video state with debouncing
            let playStateTimeout = null;
            
            video.addEventListener('play', () => {
                console.log('Video play event fired');
                if (playStateTimeout) clearTimeout(playStateTimeout);
                playStateTimeout = setTimeout(() => {
                    if (!video.paused) {
                        setIsPlaying(true);
                    }
                }, 50);
            });

            video.addEventListener('pause', () => {
                console.log('Video pause event fired');
                if (playStateTimeout) clearTimeout(playStateTimeout);
                playStateTimeout = setTimeout(() => {
                    if (video.paused) {
                        setIsPlaying(false);
                        // Trigger tool group setup when video is paused
                        setTimeout(() => {
                            setupToolGroup();
                        }, 100);
                    }
                }, 50);
            });

            video.addEventListener('ended', () => {
                console.log('Video ended event fired');
                if (playStateTimeout) clearTimeout(playStateTimeout);
                setIsPlaying(false);
            });

            console.log('AHI Video displayed with VIDEO element');

        } catch (error) {
            console.error('Error displaying AHI video:', error);
            throw error;
        }
    };

    // Display using VIDEO viewport (MP4 files)
    const displayWithVideoViewport = async (file, dicomData) => {
        try {
            // Extract MP4 data as before
            const pixelDataElement = dicomData.elements.x7fe00010;
            if (!pixelDataElement || !pixelDataElement.dataOffset) {
                throw new Error('No pixel data found in DICOM file');
            }

            const reader = new FileReader();

            reader.onload = async function (e) {
                try {
                    const arrayBuffer = e.target.result;
                    const pixelDataOffset = pixelDataElement.dataOffset;

                    // Find MP4 data start
                    const dataView = new DataView(arrayBuffer);
                    let mp4Start = pixelDataOffset;

                    const searchRange = Math.min(1000, arrayBuffer.byteLength - pixelDataOffset);
                    for (let i = 0; i < searchRange - 3; i++) {
                        if (String.fromCharCode(dataView.getUint8(pixelDataOffset + i)) === 'f' &&
                            String.fromCharCode(dataView.getUint8(pixelDataOffset + i + 1)) === 't' &&
                            String.fromCharCode(dataView.getUint8(pixelDataOffset + i + 2)) === 'y' &&
                            String.fromCharCode(dataView.getUint8(pixelDataOffset + i + 3)) === 'p') {
                            mp4Start = pixelDataOffset + i - 4;
                            break;
                        }
                    }

                    // Extract MP4 and create blob URL
                    const mp4Data = arrayBuffer.slice(mp4Start);
                    const blob = new Blob([mp4Data], { type: 'video/mp4' });
                    const videoUrl = URL.createObjectURL(blob);
                    extractedVideoUrlRef.current = videoUrl;

                    // Clear viewport
                    const element = viewportRef.current;
                    while (element.firstChild) {
                        element.removeChild(element.firstChild);
                    }

                    // Create a video element with cornerstone3D controls
                    const video = document.createElement('video');
                    video.src = videoUrl;
                    video.style.width = '100%';
                    video.style.height = '100%';
                    video.style.objectFit = 'contain';
                    video.style.backgroundColor = '#000';
                    video.controls = false;
                    video.loop = true;

                    videoElementRef.current = video;
                    element.appendChild(video);

                    // Set up event listeners
                    video.addEventListener('loadedmetadata', () => {
                        console.log('Video metadata loaded');
                        // Auto-play on load
                        video.play().then(() => {
                            console.log('Video autoplay started');
                            setIsPlaying(true);
                        }).catch(() => {
                            // Auto-play might be blocked, try muted
                            console.log('Auto-play blocked, trying muted');
                            video.muted = true;
                            video.play().then(() => {
                                console.log('Video autoplay started (muted)');
                                setIsPlaying(true);
                            }).catch(err => {
                                console.log('Auto-play failed:', err);
                            });
                        });
                    });

                    console.log('Video displayed with VIDEO element');

                } catch (error) {
                    console.error('Error setting up video:', error);
                    throw error;
                }
            };

            reader.onerror = () => {
                throw new Error('Failed to read file');
            };

            reader.readAsArrayBuffer(file);

        } catch (error) {
            console.error('Error displaying with VIDEO viewport:', error);
            throw error;
        }
    };

    // Handle preloaded file or image ID
    useEffect(() => {
        if (isInitialized) {
            if (preloadedImageId && isAHI) {
                handleFileSelect(preloadedImageId);
            } else if (preloadedFile) {
                handleFileSelect(preloadedFile);
            }
        }
    }, [preloadedFile, preloadedImageId, isInitialized, isAHI, ahiMetadata, handleFileSelect]);

    // Play/pause toggle for multiframe
    const togglePlayPause = useCallback(() => {
        if (fileInfo?.isMP4 && videoElementRef.current) {
            // For MP4 files
            const video = videoElementRef.current;
            if (video.paused) {
                // Video is paused, so play it
                video.play().then(() => {
                    console.log('Video playing');
                    setIsPlaying(true);
                }).catch(err => {
                    console.error('Error playing video:', err);
                    // Try muted if unmuted playback fails
                    video.muted = true;
                    video.play().then(() => {
                        console.log('Video playing (muted)');
                        setIsPlaying(true);
                    }).catch(err2 => {
                        console.error('Error playing video even when muted:', err2);
                    });
                });
            } else {
                // Video is playing, so pause it
                video.pause();
                console.log('Video paused');
                setIsPlaying(false);
            }
        } else if (fileInfo?.numberOfFrames > 1) {
            // For non-MP4 multiframe - toggle play state and implement frame cycling
            setIsPlaying(!isPlaying);

            if (!isPlaying) {
                // Start manual frame cycling for multiframe DICOM
                let currentFrameIndex = currentFrame - 1;
                const frameInterval = setInterval(() => {
                    currentFrameIndex = (currentFrameIndex + 1) % fileInfo.numberOfFrames;
                    const nextFrame = currentFrameIndex + 1;
                    setCurrentFrame(nextFrame);
                    
                    // Navigate to the next frame
                    navigateToFrame(nextFrame);
                }, 1000 / frameRate);

                // Store interval ID for cleanup
                animationFrameRef.current = frameInterval;
                console.log(`Started multiframe playback at ${frameRate} fps`);
            } else {
                // Stop frame cycling
                if (animationFrameRef.current) {
                    clearInterval(animationFrameRef.current);
                    animationFrameRef.current = null;
                }
                console.log('Stopped multiframe playback');
            }
        }
    }, [fileInfo, isPlaying, currentFrame, frameRate]);

    // Keyboard shortcuts for multiframe navigation
    useEffect(() => {
        const handleKeyPress = (e) => {
            // Only handle shortcuts if we have a multiframe file and no input is focused
            if (!fileInfo || fileInfo.numberOfFrames <= 1) return;
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

            const video = videoElementRef.current;

            switch (e.key) {
                case ' ':
                case 'Spacebar':
                    e.preventDefault();
                    togglePlayPause();
                    break;

                case 'ArrowLeft':
                    e.preventDefault();

                    if (video && fileInfo.isMP4 && video.duration) {
                        // MP4 video navigation
                        video.pause();

                        // Calculate exact frame time
                        const frameDuration = video.duration / totalFrames;
                        let targetFrame = currentFrame - 1;
                        if (targetFrame < 1) targetFrame = totalFrames;

                        const targetTime = (targetFrame - 1) * frameDuration;

                        // Update both frame and video time
                        setCurrentFrame(targetFrame);
                        video.currentTime = targetTime;
                    } else if (fileInfo && fileInfo.numberOfFrames > 1) {
                        // Non-MP4 multiframe navigation
                        navigateToFrame(currentFrame - 1);
                    }
                    break;

                case 'ArrowRight':
                    e.preventDefault();

                    if (video && fileInfo.isMP4 && video.duration) {
                        // MP4 video navigation
                        video.pause();

                        // Calculate exact frame time
                        const frameDuration = video.duration / totalFrames;
                        let targetFrame = currentFrame + 1;
                        if (targetFrame > totalFrames) targetFrame = 1;

                        const targetTime = (targetFrame - 1) * frameDuration;

                        // Update both frame and video time
                        setCurrentFrame(targetFrame);
                        video.currentTime = targetTime;
                    } else if (fileInfo && fileInfo.numberOfFrames > 1) {
                        // Non-MP4 multiframe navigation
                        navigateToFrame(currentFrame + 1);
                    }
                    break;

                default:
                    break;
            }
        };

        // Add event listener
        window.addEventListener('keydown', handleKeyPress);

        // Cleanup
        return () => {
            window.removeEventListener('keydown', handleKeyPress);
        };
    }, [fileInfo, totalFrames, currentFrame, togglePlayPause]);

    // Frame rate change
    const handleFrameRateChange = (e) => {
        const newFrameRate = parseInt(e.target.value, 10);
        setFrameRate(newFrameRate);

        if (fileInfo?.isMP4 && videoElementRef.current) {
            // For MP4 files, adjust playback rate
            const duration = videoElementRef.current.duration;
            if (duration && totalFrames) {
                const naturalFrameRate = totalFrames / duration;
                videoElementRef.current.playbackRate = newFrameRate / naturalFrameRate;
            }
        } else if (fileInfo?.numberOfFrames > 1) {
            // For non-MP4 multiframe
            const element = viewportRef.current;
            if (element && cornerstone.getEnabledElement(element) && isPlaying) {
                cornerstone.stopClip(element);
                cornerstone.playClip(element, newFrameRate);
            }
        }
    };

    // Frame seek
    const handleFrameSliderChange = (e) => {
        const frameNumber = parseInt(e.target.value, 10);
        const video = videoElementRef.current;

        if (video && fileInfo?.isMP4 && video.duration) {
            // Pause video first
            video.pause();

            // Update frame immediately
            setCurrentFrame(frameNumber);

            // Calculate and set video time
            const frameDuration = video.duration / totalFrames;
            const targetTime = (frameNumber - 1) * frameDuration;
            video.currentTime = targetTime;
        } else if (fileInfo && fileInfo.numberOfFrames > 1) {
            // For non-MP4 multiframe files
            setCurrentFrame(frameNumber);

            // Stop playback if playing
            if (isPlaying) {
                setIsPlaying(false);
                if (animationFrameRef.current) {
                    clearInterval(animationFrameRef.current);
                    animationFrameRef.current = null;
                }
            }

            // Navigate to the specific frame
            navigateToFrame(frameNumber);
        }
    };

    // Expose playback control methods through ref
    React.useImperativeHandle(ref, () => ({
        togglePlayPause,
        navigateFrame: (direction) => {
            const video = videoElementRef.current;
            if (video && fileInfo?.isMP4 && video.duration) {
                // Pause video first
                video.pause();

                // Calculate exact frame time
                const frameDuration = video.duration / totalFrames;
                let targetFrame = direction === 'next' ? currentFrame + 1 : currentFrame - 1;
                if (targetFrame > totalFrames) targetFrame = 1;
                if (targetFrame < 1) targetFrame = totalFrames;

                const targetTime = (targetFrame - 1) * frameDuration;

                // Update both frame and video time
                setCurrentFrame(targetFrame);
                video.currentTime = targetTime;
            }
        },
        handleFrameSliderChange,
        handleFrameRateChange,
        clearAnnotations,
        toggleAnnotationVisibility,
        getPlaybackState: () => ({
            isPlaying,
            currentFrame,
            totalFrames,
            frameRate
        })
    }));

    return (
        <div className="dicom-unified-viewer">
            {isLoading && <div className="loading">Loading DICOM file...</div>}
            {error && <div className="error">{error}</div>}


            {/* Show active measurement info */}
            {activeMeasurementType && activeTab === 1 && rightDrawerOpen && (
                <div className="measurement-overlay-info">
                    <span className="measurement-name">Measuring: {activeMeasurementType.name}</span>
                    <span className="measurement-hint">Click to place measurement points</span>
                </div>
            )}

            {/* Main content area with viewport */}
            <div className="viewer-content-area">
                {/* Viewport - only show if not in grid mode */}
                {!isGridMode && (
                    <div className="viewport-container">
                        <div 
                            className="viewport" 
                            ref={viewportRef}
                            onContextMenu={handleContextMenu}
                        ></div>
                        {/* Annotation overlay canvas */}
                        <canvas
                            ref={annotationCanvasRef}
                            className={`annotation-canvas ${annotationMode ? 'active' : ''}`}
                            onMouseDown={startDrawing}
                            onMouseMove={draw}
                            onMouseUp={stopDrawing}
                            onMouseLeave={stopDrawing}
                        />
                        
                        {/* Context Menu */}
                        {contextMenu && (
                            <div
                                style={{
                                    position: 'fixed',
                                    top: contextMenu.mouseY,
                                    left: contextMenu.mouseX,
                                    backgroundColor: '#2a2a2a',
                                    border: '1px solid #444',
                                    borderRadius: '4px',
                                    padding: '8px 0',
                                    zIndex: 1000,
                                    minWidth: '150px',
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
                                }}
                            >
                                <div
                                    style={{
                                        padding: '8px 16px',
                                        color: '#e0e0e0',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        borderBottom: '1px solid #444'
                                    }}
                                    onClick={() => handleContextMenuAction('dicomTags')}
                                    onMouseEnter={(e) => e.target.style.backgroundColor = '#3a3a3a'}
                                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                >
                                    📋 DICOM Tags
                                </div>
                                <div
                                    style={{
                                        padding: '8px 16px',
                                        color: '#888',
                                        cursor: 'not-allowed',
                                        fontSize: '14px'
                                    }}
                                    title="Coming soon"
                                >
                                    💾 Save as DICOM
                                </div>
                                <div
                                    style={{
                                        padding: '8px 16px',
                                        color: '#888',
                                        cursor: 'not-allowed',
                                        fontSize: '14px'
                                    }}
                                    title="Coming soon"
                                >
                                    🎬 Save as Video
                                </div>
                                <div
                                    style={{
                                        padding: '8px 16px',
                                        color: '#888',
                                        cursor: 'not-allowed',
                                        fontSize: '14px'
                                    }}
                                    title="Coming soon"
                                >
                                    🖼️ Save as Image
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
            
            {/* DICOM Tags Dialog */}
            <DicomTagsDialog
                open={showDicomTagsDialog}
                onClose={() => setShowDicomTagsDialog(false)}
                dicomFile={null}
                selectedFile={fileInfo}
            />
        </div>
    );
});

export default DicomUnifiedViewer;
